/**
 * Quem é o dono do push deste aparelho — o APP, não a pessoa (decisão do Augusto, 12/10/2026: "só num clique").
 *
 * O SE_WEB interno e o Portal do Parceiro são a MESMA origem e o MESMO service worker (`/service-worker.js`): o navegador tem UMA
 * inscrição de push por aparelho. O servidor aplica a R10 do passo 8 (um endpoint, um dono): quem reenvia a inscrição vira o dono. Como
 * o `ReinscricaoDoPush` roda em TODA carga dos dois apps, num navegador com sessão interna E de parceiro cada recarga tomava o push
 * do outro, calada. A marca `localStorage["seweb:push-dono"]` ("interno" | "portal") resolve: o push só troca de dono num gesto
 * explícito — ligar no cartão/convite ou entrar (login) — e a reinscrição automática respeita a marca do outro app.
 *
 * Só o app entra na marca: dentro de um app a troca de pessoa já acontece pelo login, e o "Sair" do interno apaga a linha no servidor.
 * TODO fim da sessão de um app (o "Sair" e as saídas forçadas: refresh recusado, 401, logout da manutenção) larga a marca dele; senão a
 * linha de quem saiu ficaria no servidor e o outro app, ainda logado, não poderia adotar o push (os avisos de quem saiu seguiriam chegando).
 * Melhor-esforço: o armazenamento pode lançar (aba privada, bloqueado); sem ele, cai no comportamento de antes (sem marca = adota).
 *
 * Módulo puro de propósito — sem contexto, sem axios, sem `@/services/api`: o Portal o importa e não pode tocar a API interna (um 401
 * interno apaga o `seweb_auth`).
 */
import {
  limparConfirmacaoDoPush,
  limparSincronizacao,
  zerarFalhasDoReligar,
} from "./memoriaDoPush";

/** Onde a marca mora no navegador. */
export const CHAVE_DO_DONO_DO_PUSH = "seweb:push-dono";

export type AppDoPush = "interno" | "portal";

/**
 * Igual ao `PUSH_BASE_PORTAL` de `hooks/clienteDoPush.ts`, repetido aqui de propósito: importá-lo traria os dois axios (inclusive o
 * interno) para o Portal. `donoDoPush.test.ts` confere que os dois valores são o mesmo.
 */
const BASE_DO_PUSH_DO_PORTAL = "/portal/push";

/** Qual app fala neste `basePath` de push: o do portal é o portal; qualquer outro valor é o interno. */
export function appDoPush(basePath: string): AppDoPush {
  return basePath === BASE_DO_PUSH_DO_PORTAL ? "portal" : "interno";
}

/** O app que é dono do push deste navegador, ou `null` (sem marca, valor desconhecido ou armazenamento indisponível). */
export function lerDonoDoPush(): AppDoPush | null {
  try {
    const marca = window.localStorage.getItem(CHAVE_DO_DONO_DO_PUSH);
    return marca === "interno" || marca === "portal" ? marca : null;
  } catch {
    return null;
  }
}

/** Passa o push deste navegador para o `app` (ligar no cartão, entrar, ou adoção na primeira reinscrição). */
export function marcarDonoDoPush(app: AppDoPush): void {
  try {
    window.localStorage.setItem(CHAVE_DO_DONO_DO_PUSH, app);
  } catch {
    // sem armazenamento: o push segue funcionando, só sem a proteção contra a troca calada
  }
}

/**
 * Larga a marca SE ela for do `app`: quem sai (ou desliga) não solta o push de quem ficou com ele. E esquece a parte da SESSÃO do app
 * na memória do aparelho (confirmação do servidor, carimbo da sincronização, falhas do religar), sempre, com ou sem marca.
 */
export function soltarDonoDoPush(app: AppDoPush): void {
  // Toda saída de sessão passa por aqui, então nada disso atravessa a troca de pessoa e o intervalo mínimo nunca esconde um login novo.
  // A intenção (ligado/desligado) e a marca da religação única são do APARELHO e não saem (ver `memoriaDoPush.ts`).
  limparConfirmacaoDoPush(app);
  limparSincronizacao(app);
  zerarFalhasDoReligar(app);
  try {
    if (lerDonoDoPush() === app) window.localStorage.removeItem(CHAVE_DO_DONO_DO_PUSH);
  } catch {
    // idem `marcarDonoDoPush`
  }
}

/** A reinscrição automática do `app` pode reenviar? Sim sem marca (adoção) ou com a marca dele; não com a marca do outro app. */
export function podeRenovarOPush(app: AppDoPush): boolean {
  const dono = lerDonoDoPush();
  return dono === null || dono === app;
}
